import React, { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Spinner,
  Center,
  Button,
  useToast,
  FormControl,
  FormLabel,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import moment from 'moment';
import BullionDeskAPI from 'api/bulliondesk';
import Card from 'components/card/Card';
import InputField from 'components/formComponents/inputField.js';
import { MetalTypesOptions } from 'config/constant';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import { BullionDeskValidationSchema } from 'formValidations/bullionDeskFormValidations';
import PageError from 'components/nopermission/PageError';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';

export default function AboutBullionDesk(props) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const toastIdRef = React.useRef();
  const [disabled, setDisabled] = useState(true);
  const [bullionDeskData, setBullionDeskData] = useState({});
  const enable = () => setDisabled(!disabled);
  const disable = () => setDisabled(true);
  const { getPermissionsForModule } = usePermissions();
  const [bullionDeskUpdatePermissions, setBullionDeskUpdatePermissions] =
    useState(null);
  const [fetchBullionDataError, setFetchBullionDataError] = useState(false);
  const bullionDeskId = IdFromUrl(window.location.href);

  function IdFromUrl(url) {
    const parts = url.split('/');
    const id = parts[parts.length - 1];
    return id;
  }
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const priceTableData = [
    {
      fieldName: 'Threshold Buy',
      fieldValue: bullionDeskData.threshold_buy,
      usedValue: bullionDeskData.threshold_buy_used,
      addon: bullionDeskData.threshold_buy_addon,
    },
    {
      fieldName: 'Threshold Sell',
      fieldValue: bullionDeskData.threshold_sell,
      usedValue: bullionDeskData.threshold_sell_used,
      addon: bullionDeskData.threshold_sell_addon,
    },
  ];

  const FetchBullionDeskData = async () => {
    if (!isValidUUID(bullionDeskId)) {
      setFetchBullionDataError(true);
      return;
    }

    setLoading(true);
    try {
      let response = await BullionDeskAPI.GetById(bullionDeskId);
      response = response.data;

      if (response.status_code === 200) {
        setBullionDeskData(response?.data);
        setFetchBullionDataError(false);
      } else {
        setFetchBullionDataError(true);
        setBullionDeskData({});
      }
    } catch (err) {
      setFetchBullionDataError(true);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bullionDeskUpdatePermissions = getPermissionsForModule([
      Modules.BullionDesk,
    ]);
    setBullionDeskUpdatePermissions(bullionDeskUpdatePermissions);
    FetchBullionDeskData();
  }, [getPermissionsForModule]);

  const initialValues = {
    operation_location: bullionDeskData?.operation_location,
    start: bullionDeskData?.start,
    expiry: bullionDeskData?.expiry,
    type: bullionDeskData?.type,
    unit: bullionDeskData?.unit,
    sell: bullionDeskData?.sell,
    buy: bullionDeskData?.buy,
    // exchange: bullionDeskData?.exchange,
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    console.log(
      'handlesubmit called from bulliondeskupdate with values =',
      values
    );
    try {
      const api = await BullionDeskAPI.Update({
        bullionDeskId,
        ...values,
      });
      const response = api.data;
      if (response.status_code === 200) {
        toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 1000,
          isClosable: true,
          position: 'top',
        });
        setDisabled(true);
      } else {
        toast({
          title: response.message,
          status: 'error',
          duration: 1000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      toast({
        title: 'Unable to Update',
        status: 'error',
        duration: 1000,
        isClosable: true,
        position: 'top',
      });
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!bullionDeskUpdatePermissions?.read) {
    return <NoPermissionsMessage module={Modules.BullionDesk} />;
  }

  return loading ? (
    <Center
      mt='10rem'
      width='100%'
      // bgColor='red'
    >
      <Spinner size='xl' />
    </Center>
  ) : fetchBullionDataError ? (
    <PageError />
  ) : (
    <Flex
      maxWidth='7xl'
      flexDirection='column'
      gap={8}
      mt='10rem'>
      <Flex justifyContent='space-between'>
        {/* <Text
          fontSize='3xl'
          fontWeight='400'>
          Bullion Desk for
        </Text>
        {bullionDeskUpdatePermissions?.update && bullionDeskData.is_active ? (
          disabled ? (
            <EditIcon
              ml='5'
              fontSize='large'
              onClick={enable}
              cursor='pointer'
            />
          ) : (
            <CloseIcon
              ml='5'
              fontSize='large'
              onClick={disable}
              cursor='pointer'
            />
          )
        ) : (
          <></>
        )} */}
      </Flex>

      <FormControl>
        <FormLabel
          fontSize='2xl'
          fontWeight='400'>
          Location : {initialValues.operation_location}
        </FormLabel>
        <FormLabel>
          Duration : {moment(initialValues.start).format('DD MMM YY, HH:mm')} to{' '}
          {moment(initialValues.expiry).format('DD MMM YY, HH:mm')}{' '}
        </FormLabel>
        <FormLabel>
          Metal Type :{' '}
          {
            MetalTypesOptions.find(
              (option) => option.value === initialValues.type
            )?.label
          }
        </FormLabel>
        <FormLabel>Unit : {initialValues.unit}</FormLabel>
      </FormControl>
      <Formik
        initialValues={initialValues}
        validationSchema={BullionDeskValidationSchema}
        onSubmit={handleUpdate}
        enableReinitialize>
        {({ isSubmitting }) => (
          <Form noValidate>
            <Flex
              height='100%'
              direction='column'>
              <Card
                mt={4}
                direction='column'
                bgColor='gray.200'
                w='100%'
                minHeight='12rem'
                maxHeight='42rem'
                px='0px'
                overflowX='scroll'>
                <Flex overflow='visible'>
                  <Table
                    variant='simple'
                    color='gray.500'
                    mb='24px'>
                    <Thead>
                      <Tr>
                        <Th
                          pe='10px'
                          borderColor={borderColor}>
                          <Flex
                            justify='space-between'
                            align='center'
                            fontSize={{ sm: '10px', lg: '12px' }}
                            color='gray.500'></Flex>
                        </Th>
                        <Th
                          pe='10px'
                          borderColor={borderColor}>
                          <Flex
                            justify='space-between'
                            align='center'
                            fontSize={{ sm: '10px', lg: '12px' }}
                            color='gray.500'>
                            Stock(gm)
                          </Flex>
                        </Th>

                        <Th
                          pe='10px'
                          borderColor={borderColor}>
                          <Flex
                            justify='space-between'
                            align='center'
                            fontSize={{ sm: '10px', lg: '12px' }}
                            color='gray.500'>
                            Used(gm)
                          </Flex>
                        </Th>

                        <Th
                          pe='10px'
                          borderColor={borderColor}>
                          <Flex
                            justify='space-between'
                            align='center'
                            fontSize={{ sm: '10px', lg: '12px' }}
                            color='gray.500'>
                            Addon(Rs)
                          </Flex>
                        </Th>
                      </Tr>
                    </Thead>

                    <Tbody>
                      {priceTableData?.map((obj, index) => {
                        return (
                          // <Link to={{ pathname: '/permission-group', state: obj.id }}>
                          <Tr key={obj.id}>
                            <Td>
                              <Flex align='center'>
                                <Text
                                  me='10px'
                                  color={textColor}
                                  fontSize='sm'
                                  fontWeight='700'>
                                  {obj.fieldName}
                                </Text>
                              </Flex>
                            </Td>
                            <Td>
                              <Flex align='center'>
                                <Text
                                  me='10px'
                                  color={textColor}
                                  fontSize='sm'
                                  fontWeight='700'>
                                  {obj.fieldValue}
                                </Text>
                              </Flex>
                            </Td>

                            <Td>
                              <Flex align='center'>
                                <Text
                                  me='10px'
                                  color={textColor}
                                  fontSize='sm'
                                  fontWeight='700'>
                                  {obj.usedValue}
                                </Text>
                              </Flex>
                            </Td>

                            <Td>
                              <Flex align='center'>
                                <Text
                                  me='10px'
                                  color={textColor}
                                  fontSize='sm'
                                  fontWeight='700'>
                                  {obj.addon}
                                </Text>
                              </Flex>
                            </Td>
                          </Tr>
                          // </Link>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Flex>
              </Card>
            </Flex>

            <Card mt={8}>
              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}>
                <InputField
                  label='Customer Sell Price'
                  name='sell'
                  type='number'
                  // placeholder={initialValues.sell}
                  required={!disabled && true}
                  variant='auth'
                  bgColor={disabled ? 'gray.200' : ''}
                  disabled={disabled}
                />
                <InputField
                  label='Customer Buy Price'
                  name='buy'
                  type='number'
                  // placeholder={initialValues.buy}
                  required={!disabled && true}
                  variant='auth'
                  bgColor={disabled ? 'gray.200' : ''}
                  disabled={disabled}
                />
                {/* <InputField
                  label='Customer Exchange Price'
                  name='exchange'
                  type='number'
                  // placeholder={initialValues.exchange}
                  required={!disabled && true}
                  variant='auth'
                  bgColor={disabled ? 'gray.200' : ''}
                  disabled={disabled}
                /> */}
              </Flex>
            </Card>

            {!disabled && (
              <Flex>
                <Button
                  mt={20}
                  colorScheme='blue'
                  type='submit'
                  isLoading={isSubmitting}>
                  Update Bullion Desk
                </Button>
              </Flex>
            )}
          </Form>
        )}
      </Formik>
    </Flex>
  );
}
